import { USAGE_EVENT_MAX_PROPERTIES_PER_EVENT } from '@monetize/constants';
import {
  IUsageEventRequestSchema,
  UsageEventRequestSchema,
} from '@monetize/types/app';
import { v4 as uuidv4 } from 'uuid';
import { extractPropertiesFromCsvRow } from './extractPropertiesFromCsvRow';

export const PROPERTIES_COLUMN_PREFIX = 'properties.';

export const CSV_FIELDS = [
  'id',
  'timestamp',
  'accountId',
  'subscriptionId',
  'usageTypeId',
  'unitsConsumed',
  'ratedPrice',
];

/**
 * Header row for the downloadable usage template: the standard columns plus one
 * `properties.<key>` column per configured usage event custom field.
 */
export function getUsageTemplateFields(
  configuredPropertyKeys: string[],
  enableUsageCustomFields: boolean,
): string[] {
  if (!enableUsageCustomFields) {
    return [...CSV_FIELDS];
  }
  return [
    ...CSV_FIELDS,
    ...configuredPropertyKeys.map((key) => `${PROPERTIES_COLUMN_PREFIX}${key}`),
  ];
}

export interface ProcessUsageCsvInput {
  rows: Partial<IUsageEventRequestSchema>[];
  fields: string[];
  recordData: { accountId?: string; subscriptionId?: string };
  enableUsageCustomFields: boolean;
  /**
   * Keys of the tenant's configured USAGE_EVENT custom fields. The usage
   * service silently drops any other property, so columns outside this list
   * are surfaced as warnings.
   */
  configuredPropertyKeys?: string[];
}

export type ProcessUsageCsvResult =
  | { status: 'error'; errors: string[] }
  | {
      status: 'success';
      results: IUsageEventRequestSchema[];
      warnings: string[];
    };

export function processUsageCsvParse({
  rows,
  fields,
  recordData,
  enableUsageCustomFields,
  configuredPropertyKeys = [],
}: ProcessUsageCsvInput): ProcessUsageCsvResult {
  const configuredKeySet = new Set(configuredPropertyKeys);
  const requiredColumns = [
    'timestamp',
    'usageTypeId',
    'unitsConsumed',
    ...(!recordData.accountId ? ['accountId'] : []),
    ...(!recordData.subscriptionId ? ['subscriptionId'] : []),
  ];
  const missingErrors = requiredColumns
    .filter((col) => !fields.includes(col))
    .map((col) => `Missing required column '${col}'.`);
  if (missingErrors.length > 0) {
    return { status: 'error', errors: missingErrors };
  }

  const warnings = fields.reduce<string[]>((acc, col) => {
    if (CSV_FIELDS.includes(col)) {
      return acc;
    }
    if (enableUsageCustomFields && col.startsWith(PROPERTIES_COLUMN_PREFIX)) {
      const key = col.slice(PROPERTIES_COLUMN_PREFIX.length).trim();
      if (key && !configuredKeySet.has(key)) {
        acc.push(
          `Unrecognized custom field '${col}'. It is not configured in Settings > Custom Fields and will be dropped by the server.`,
        );
      }
      return acc;
    }
    acc.push(`Unrecognized column '${col}'. This column will be ignored.`);
    return acc;
  }, []);

  const { results, errors: rowErrors } = rows.reduce(
    (acc, row, i) => {
      // +2 to account for header row and 0-based index
      const rowNumber = i + 2;
      let properties: Record<string, string> | undefined;
      if (enableUsageCustomFields) {
        const extracted = extractPropertiesFromCsvRow(
          row as Record<string, unknown>,
        );
        if (extracted.invalidKeys.length > 0) {
          acc.errors.push(
            `Row ${rowNumber}: Invalid custom property name(s): ${extracted.invalidKeys.join(
              ', ',
            )}. Use letters, digits, and underscores only; must start with a letter.`,
          );
          return acc;
        }
        if (extracted.reservedKeys.length > 0) {
          acc.errors.push(
            `Row ${rowNumber}: Reserved custom property name(s): ${extracted.reservedKeys.join(
              ', ',
            )}. These names are used by the usage service and cannot be custom properties.`,
          );
          return acc;
        }
        const propertyCount = Object.keys(extracted.properties ?? {}).length;
        if (propertyCount > USAGE_EVENT_MAX_PROPERTIES_PER_EVENT) {
          acc.errors.push(
            `Row ${rowNumber}: Too many custom properties (${propertyCount}). A usage event supports at most ${USAGE_EVENT_MAX_PROPERTIES_PER_EVENT}.`,
          );
          return acc;
        }
        properties = extracted.properties;
      }
      const result = UsageEventRequestSchema.safeParse({
        id: row.id || uuidv4().slice(0, 23),
        accountId: row.accountId || recordData.accountId || '',
        subscriptionId: row.subscriptionId || recordData.subscriptionId || '',
        usageTypeId: row.usageTypeId,
        timestamp: row.timestamp,
        unitsConsumed: row.unitsConsumed || 0,
        ratedPrice: row.ratedPrice,
        properties,
      });
      if (!result.success) {
        acc.errors.push(
          `Row ${rowNumber}: ${result.error.issues[0].message || result.error.message}`,
        );
      } else {
        acc.results.push(result.data);
      }
      return acc;
    },
    { results: [], errors: [] } as {
      results: IUsageEventRequestSchema[];
      errors: string[];
    },
  );

  if (rowErrors.length > 0) {
    return { status: 'error', errors: rowErrors };
  }

  return { status: 'success', results, warnings };
}
